import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import i18n from '@src/core/i18n';
import { useTotpSetupMutation, useTotpVerifyMutation } from '@src/features/auth/redux';
import { ACCESS_TOKEN_KEY } from '@src/core/config';
import { routesConfig } from '@src/core/routes';
import { trackError, toTrackedError } from '@src/core/sentry';

export interface IMfaEnrollmentForm {
  code: string;
}

// Drives the forced TOTP enrollment gate. The user is already authenticated (the Bearer token
// is in localStorage), but any /api/** call returns 412 + X-MFA-Enrollment-Required until TOTP
// is enabled. Flow: setup (QR + secret) -> verify (6-digit code) -> show backup codes once ->
// continue into the app.
export const useMfaEnrollment = () => {
  const navigate = useNavigate();
  const [totpSetup, { data: setupData, isLoading: isSettingUp }] = useTotpSetupMutation();
  const [totpVerify, { isLoading: isVerifying }] = useTotpVerifyMutation();
  const form = useForm<IMfaEnrollmentForm>({ defaultValues: { code: '' } });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  // No session token here means the user never authenticated — send them to sign in.
  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) {
      navigate(routesConfig.signIn, { replace: true });
      return;
    }
    totpSetup()
      .unwrap()
      .catch((err: unknown) => {
        // A 412 here means this session can't enroll — typically a stale session whose
        // account already has 2FA enabled elsewhere. Enrollment is a dead end for it, so
        // drop the stale token and send the user back to sign in, where the normal
        // two-step login routes them to TOTP verification instead of looping on this gate.
        const status = (err as { status?: number })?.status;
        if (status === 412) {
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          navigate(routesConfig.signIn, { replace: true });
          return;
        }
        trackError(toTrackedError(err), { code: 'FE_0022', extra: { flow: 'totpSetup' } });
        setErrorMessage(i18n.t('mfa.errors.qrFailed'));
      });
  }, [navigate, totpSetup]);

  const onSubmit = async (data: IMfaEnrollmentForm) => {
    setErrorMessage(null);
    try {
      const response = await totpVerify({ code: data.code.trim() }).unwrap();
      setBackupCodes(response.backupCodes);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 401 || status === 400) {
        setErrorMessage(i18n.t('mfa.errors.MFA_CODE_INVALID'));
        form.setValue('code', '');
        return;
      }
      setErrorMessage(i18n.t('mfa.errors.verifyFailed'));
      trackError(toTrackedError(err), { code: 'FE_0022', extra: { flow: 'totpVerify' } });
    }
  };

  // Called after the user confirms they saved their backup codes.
  const onFinish = () => {
    navigate(routesConfig.app, { replace: true });
  };

  return {
    ...form,
    onSubmit: form.handleSubmit(onSubmit),
    setupData,
    isSettingUp,
    isVerifying,
    errorMessage,
    backupCodes,
    onFinish,
  };
};
